import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Sort } from '@angular/material/sort';
import { SocketEventType } from '@core';
import { type IncidentModel } from '@models';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntities, removeAllEntities, removeEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { SocketEvents } from '@ngrx-store';
import type { Subscription } from 'rxjs';
import { firstValueFrom as toPromise } from 'rxjs';

import { DeviceTableCols, type NormalizedDeviceData } from '../models';
import { DeviceServiceV2 } from '../services';

const defaultSort = {
  active: DeviceTableCols.DEVICE_NAME,
  direction: 'asc',
} as Sort;

export const DevicesStore = signalStore(
  withEntities<NormalizedDeviceData>(),
  withState({
    count: null as number | null,
    offset: 0 as number,
    sort: defaultSort,
  }),
  withMethods((store, service = inject(DeviceServiceV2)) => {
    let listSubscription: Subscription | null;

    return {
      clear() {
        patchState(store, removeAllEntities(), {
          count: null,
          offset: 0,
        });
      },

      async deleteDevice(device: NormalizedDeviceData) {
        await toPromise(service.deleteDevice(device));

        this.dropDevice(device.id);
      },

      dropDevice(id: string) {
        patchState(store, removeEntity(id));
      },

      async loadDevices(params: { filters?: object; query?: string; sort?: Sort; }) {
        if (store.count() === store.offset()) {
          return;
        }

        const offset = store.offset() ?? 0;
        const { promise, resolve } = Promise.withResolvers<void>();

        listSubscription?.unsubscribe();

        listSubscription = service.list({
          ...params,
          limit: 70,
          offset: store.offset(),
        }).subscribe(({ count, data }) => {
          patchState(
            store,
            addEntities(data),
            { count, offset: offset + data.length },
          );

          resolve();
        });

        return promise;
      },

      replaceData(data: NormalizedDeviceData[]) {
        patchState(store, removeAllEntities(), addEntities(data), {
          count: data.length,
          offset: data.length,
        });
      },

      updateDeviceEntity(id: string, changes: Partial<NormalizedDeviceData>) {
        patchState(store, updateEntity({
          changes,
          id,
        }));
      },

      updateSort(sort: Sort) {
        if (sort.direction === '') {
          sort = defaultSort;
        }

        patchState(store, { sort });
      },
    };
  }),

  withHooks((store) => ({
    onInit() {
      SocketEvents
        .pipe(
          takeUntilDestroyed(),
        )
        .subscribe(({ data, type }) => {
          if (type === SocketEventType.DEVICE_UPDATED && !!store.entityMap()[data.id]) {
            store.updateDeviceEntity(data.id, { latestStreamingTimestamp: data.latestStreamingTimestamp });

            return;
          }

          if (type === SocketEventType.INCIDENT_CREATED) {
            const incidents: IncidentModel[] = Array.isArray(data) ? data : [data];

            incidents.forEach(incident => {
              const deviceId = incident.device.id;

              if (store.entityMap()[deviceId]) {
                const current = store.entityMap()[deviceId];

                store.updateDeviceEntity(deviceId, {
                  incidents: [...(current.incidents ?? []), incident],
                });
              }
            });
          }

          if (type === SocketEventType.INCIDENT_RESOLVED) {
            const resolved: IncidentModel[] = data.data ?? [data];

            resolved.forEach(incident => {
              const deviceId = incident.device?.id;

              if (deviceId && store.entityMap()[deviceId]) {
                const current = store.entityMap()[deviceId];

                store.updateDeviceEntity(deviceId, {
                  incidents: (current.incidents ?? []).filter(i => i.id !== incident.id),
                });
              }
            });
          }
        });
    },
  })),
);
